import React, { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * ExportButton Component
 * Exports presentation to PDF
 * 
 * Features:
 * - Multi-page PDF export
 * - Progress indicator
 * - High-quality rendering
 * - Custom filename
 */
const ExportButton = ({ reportData, reportId, deckRef }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const exportToPDF = async () => {
    if (!deckRef.current || isExporting) return;

    setIsExporting(true);
    setProgress(0);

    try {
      const deck = deckRef.current;
      
      // Find all slide elements - try multiple selectors
      let slides = deck.querySelectorAll(".slide-container > div");
      
      if (slides.length === 0) {
        slides = deck.querySelectorAll("[class*=\"slide\"]");
      }
      
      if (slides.length === 0) {
        slides = deck.querySelectorAll("section");
      }

      if (slides.length === 0) {
        throw new Error("Keine Slides gefunden");
      }

      console.log("[EXPORT] Found", slides.length, "slides");

      // Create PDF (landscape A4)
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [1920, 1080]
      });

      // Capture each slide
      for (let i = 0; i < slides.length; i++) {
        setProgress(Math.round(((i + 1) / slides.length) * 100));

        console.log("[EXPORT] Capturing slide", i + 1, "of", slides.length);

        // Wait for any animations or images to load
        await new Promise(resolve => setTimeout(resolve, 300));

        // Capture slide
        const canvas = await html2canvas(slides[i], {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#1a1a1a"
        });

        const imgData = canvas.toDataURL("image/png");

        // Add page (except for first slide)
        if (i > 0) {
          pdf.addPage();
        }

        // Add image to PDF
        pdf.addImage(imgData, "PNG", 0, 0, 1920, 1080);
      }

      // Generate filename
      const propertyName = reportData?.metadata?.propertyName || reportData?.property?.name || reportData?.property?.street_address || "Immobilie";
      const date = new Date().toISOString().split("T")[0];
      const filename = `Bankreport_${propertyName.replace(/\s+/g, "_")}_${date}.pdf`;

      // Save PDF
      pdf.save(filename);
      console.log("[EXPORT] PDF saved:", filename);
    } catch (error) {
      console.error("[EXPORT] Error:", error);
      alert("Fehler beim Exportieren: " + error.message);
    } finally {
      setIsExporting(false);
      setProgress(0);
    }
  };

  return (
    <button
      onClick={exportToPDF}
      disabled={isExporting}
      className={`relative px-4 py-2 bg-white/90 hover:bg-white text-gray-800 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200 ${
        isExporting ? "opacity-50 cursor-not-allowed" : "hover:scale-105"
      }`}
      title="Als PDF exportieren"
    >
      <div className="flex items-center space-x-2">
        {isExporting ? (
          <>
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">{progress}%</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm font-medium">PDF</span>
          </>
        )}
      </div>
    </button>
  );
};

export default ExportButton;
